import React, { useState, useEffect, useCallback } from 'react';
import PagesWarning from '@/components/documents/PagesWarning';
import SourceBadge from '@/components/documents/SourceBadge';
import { FileText, Upload, Eye, CheckCircle, XCircle, Clock, AlertTriangle, Search, Edit2, Sparkles, Check, ChevronsUpDown, Trash2, Download, MailCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { documentAPI, accountingAPI, exportAPI, downloadBlob } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/tooltip';
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '../../components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../components/ui/popover';
import { BilagViewer } from '../../components/documents/BilagViewer';
import { documentLinesAPI } from '../../lib/api';
import { isFieldUncertain, fieldInputClass, fieldLabelClass, vatContradiction } from '../../components/documents/ConfidenceField';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDate, formatCurrency, getStatusBadge, calculateConfidenceColor } from '../../lib/utils';

const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const item = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0 }
};

// Field display names
const FIELD_LABELS = {
    supplier_name: 'Leverandør',
    cvr_number: 'CVR-nr.',
    invoice_number: 'Fakturanr.',
    invoice_date: 'Fakturadato',
    due_date: 'Forfaldsdato',
    net_amount: 'Beløb ekskl. moms',
    vat_amount: 'Momsbeløb',
    total_amount: 'Total',
    currency: 'Valuta',
    // vat_percentage is deliberately absent: it is not in the live extraction schema and was
    // empty on every document, so it could never populate. An always-blank field teaches the
    // accountant to distrust the panel.
    // account_code / account_name are intentionally NOT free-text fields here —
    // they are edited via the dedicated account-override picker in the
    // "Suggested Account" block (real chart of accounts).
};

const DEFAULT_INTAKE = [
    { key: 'expense_receipt', label: 'Expense receipt', doc_category: 'invoice', document_type_hint: 'purchase' },
    { key: 'income_settlement', label: 'Income settlement', doc_category: 'invoice', document_type_hint: 'sale' },
    { key: 'bank_statement', label: 'Bank statement', doc_category: 'bank_statement', document_type_hint: 'bank_statement' },
    { key: 'other', label: 'Other document', doc_category: 'other', document_type_hint: null },
];

export default function DocumentsPage() {
    const { currentTenant, tenants } = useTenant();
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [intakeKey, setIntakeKey] = useState('expense_receipt');
    const [categories, setCategories] = useState(DEFAULT_INTAKE);
    const intakeCat = categories.find((c) => c.key === intakeKey) || categories[0] || {};
    const uploadCategory = intakeCat.doc_category || 'invoice';
    const docTypeHint = intakeCat.document_type_hint || null;
    // Per-file progress for bulk upload: [{ name, status: 'queued'|'uploading'|'done'|'failed', error }]
    const [uploadQueue, setUploadQueue] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [savingEdits, setSavingEdits] = useState(false);
    // The POSTING LINE is what actually reaches e-conomic / Uniconta — the extraction fields above
    // are only what was read off the page. Both are loaded and saved here.
    const [docLine, setDocLine] = useState(null);
    const [lineMeta, setLineMeta] = useState(null);
    const [lineEdits, setLineEdits] = useState({});
    const [clientChart, setClientChart] = useState({ accounts: [], note: null });
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [deletingDoc, setDeletingDoc] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [pending, setPending] = useState(null);
    const [exportingBatch, setExportingBatch] = useState(false);

    const openExport = async () => {
        if (!currentTenant) return;
        setExportOpen(true); setPending(null);
        try {
            const res = await exportAPI.kassekladdePending(currentTenant.id);
            setPending(res.data);
        } catch (e) {
            toast.error('Could not load exportable documents');
            setPending({ exportable: [], skipped: [], count: 0, rows: 0 });
        }
    };

    const runBatchExport = async () => {
        if (!currentTenant) return;
        setExportingBatch(true);
        try {
            const res = await exportAPI.downloadKassekladde(currentTenant.id, null);
            downloadBlob(res, 'kassekladde.csv');
            const docs = res.headers?.['x-export-docs'], rows = res.headers?.['x-export-rows'];
            toast.success(`Exported ${docs || ''} document(s), ${rows || ''} line(s) — import into e-conomic`);
            setExportOpen(false);
            fetchDocuments();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Export failed');
        } finally { setExportingBatch(false); }
    };

    const handleDeleteDoc = async () => {
        if (!docToDelete) return;
        setDeletingDoc(true);
        try {
            await documentAPI.deleteDocument(docToDelete.id);
            toast.success('Document deleted');
            setDocToDelete(null);
            fetchDocuments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete document');
        } finally {
            setDeletingDoc(false);
        }
    };
    const [editedFields, setEditedFields] = useState({});
    const [approving, setApproving] = useState(false);
    // Real chart of accounts + the accountant's account/VAT override for the open doc
    const [chart, setChart] = useState([]);
    const [accountOverride, setAccountOverride] = useState({ account_code: '', account_name: '', vat_code: '' });
    const [accountPickerOpen, setAccountPickerOpen] = useState(false);

    // Unique non-empty moms codes from the real chart (for the manual VAT override select)
    const momsCodes = React.useMemo(
        () => Array.from(new Set(chart.map(a => a.moms).filter(Boolean))).sort(),
        [chart]
    );

    const fetchDocuments = useCallback(async () => {
        if (!currentTenant) {
            setDocuments([]);
            setLoading(false);
            return;
        }

        try {
            const status = statusFilter === 'all' ? null : statusFilter;
            const response = await documentAPI.getAll(currentTenant.id, status, 'invoice');
            setDocuments(response.data);
        } catch (err) {
            console.error('Failed to fetch documents:', err);
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    }, [currentTenant, statusFilter]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Processing-queue progress (Part B): poll while anything is pending so the accountant sees
    // movement during a large run instead of a frozen screen. Stops polling when the queue drains.
    const [queue, setQueue] = useState(null);
    useEffect(() => {
        if (!currentTenant) { setQueue(null); return; }
        let active = true;
        let timer = null;
        const tick = async () => {
            try {
                const r = await documentAPI.queueStatus(currentTenant.id);
                if (!active) return;
                setQueue(r.data);
                const ms = r.data && r.data.pending > 0 ? 3000 : 15000;   // fast while pending, slow when idle
                timer = setTimeout(tick, ms);
            } catch (e) {
                if (active) timer = setTimeout(tick, 15000);
            }
        };
        tick();
        return () => { active = false; if (timer) clearTimeout(timer); };
    }, [currentTenant]);

    // Load the real chart of accounts + intake categories once
    useEffect(() => {
        let active = true;
        accountingAPI.getRealChart()
            .then(res => { if (active) setChart(res.data.accounts || []); })
            .catch(err => { console.error('Failed to load chart of accounts:', err); });
        accountingAPI.getClassification()
            .then(res => { if (active && res.data?.intake_categories?.length) setCategories(res.data.intake_categories); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Bulk upload: each file is sent through the EXISTING single-file endpoint
    // (one POST + one background OCR/AI task + one document per file). Files are
    // uploaded in small concurrent batches so the server isn't overwhelmed, and
    // each file is independent — one failure never stops the rest. Works for a
    // single file too (a batch of one).
    const handleFilesUpload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        if (!currentTenant) {
            toast.error('Please select a company first');
            return;
        }

        const UPLOAD_CONCURRENCY = 3;
        // Bank statements accept CSV (for reconciliation import) OR PDF (filed/printed);
        // a CSV's file.type is often 'text/csv' or empty, so also accept by extension.
        const isValid = (file) => {
            const name = (file.name || '').toLowerCase();
            if (uploadCategory === 'bank_statement') {
                return file.type === 'application/pdf' || name.endsWith('.pdf')
                    || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || name.endsWith('.csv');
            }
            return ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
        };

        // Build the per-file queue; invalid types are pre-marked failed (skipped, not blocking).
        const items = files.map((file) => ({
            file,
            valid: isValid(file),
        }));
        setUploadQueue(items.map((it) => ({
            name: it.file.name,
            status: it.valid ? 'queued' : 'failed',
            error: it.valid ? null : (uploadCategory === 'bank_statement' ? 'Bank statement must be CSV or PDF' : 'Unsupported file type (JPG, PNG, or PDF only)'),
        })));
        setUploading(true);

        const setItemStatus = (idx, status, error = null) => {
            setUploadQueue((prev) => prev.map((it, i) => (i === idx ? { ...it, status, error } : it)));
        };

        const pending = items
            .map((it, idx) => idx)
            .filter((idx) => items[idx].valid);

        let cursor = 0;
        let succeeded = 0;
        let failed = items.length - pending.length; // invalid-type files already counted as failed
        let sinceRefresh = 0;

        const worker = async () => {
            while (cursor < pending.length) {
                const idx = pending[cursor++];
                setItemStatus(idx, 'uploading');
                try {
                    await documentAPI.upload(items[idx].file, currentTenant.id, uploadCategory, docTypeHint);
                    setItemStatus(idx, 'done');
                    succeeded += 1;
                } catch (err) {
                    setItemStatus(idx, 'failed', err.response?.data?.detail || 'Upload failed');
                    failed += 1;
                }
                // Refresh the queue periodically so new documents appear as they land.
                sinceRefresh += 1;
                if (sinceRefresh >= UPLOAD_CONCURRENCY) {
                    sinceRefresh = 0;
                    fetchDocuments();
                }
            }
        };

        await Promise.all(
            Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pending.length) }, () => worker())
        );

        fetchDocuments();
        setUploading(false);

        if (failed === 0) {
            toast.success(`Uploaded ${succeeded} file${succeeded === 1 ? '' : 's'}. AI is extracting data...`);
        } else if (succeeded === 0) {
            toast.error(`All ${failed} file${failed === 1 ? '' : 's'} failed to upload.`);
        } else {
            toast.warning(`Uploaded ${succeeded} of ${files.length}. ${failed} failed — see the list.`);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (!uploading && e.dataTransfer?.files?.length) {
            handleFilesUpload(e.dataTransfer.files);
        }
    };

    const openDocumentDetail = async (doc) => {
        try {
            const response = await documentAPI.getOne(doc.id);
            const fullDoc = response.data;
            setSelectedDoc(fullDoc);
            setEditedFields({});
            setIsEditing(false);
            // Seed the account override from the AI's suggestion so Approve sends
            // the AI value unchanged unless the accountant overrides it.
            const ai = fullDoc.ai_suggestions || {};
            setAccountOverride({
                account_code: ai.account_code || '',
                account_name: ai.account_name || '',
                vat_code: ai.vat_code || '',
            });
            setIsDetailOpen(true);
        } catch (err) {
            toast.error('Failed to load document details');
        }
    };

    const handleFieldEdit = (field, value) => {
        setEditedFields(prev => ({ ...prev, [field]: value }));
    };

    // Accountant picks a real account: auto-fill name and the matching moms code.
    const handleAccountSelect = (account) => {
        setAccountOverride({
            account_code: String(account.konto),
            account_name: account.navn,
            vat_code: account.moms || '',
        });
        setAccountPickerOpen(false);
    };

    // Manual VAT/moms override (independent of the account's default moms).
    const handleVatOverride = (vat_code) => {
        setAccountOverride(prev => ({ ...prev, vat_code }));
    };

    // Deep link from the review screen's empty state: /app/documents/all?upload=1 opens the
    // file picker directly, so "Upload bilag" lands on the action rather than just the page.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('upload') !== '1') return;
        const t = setTimeout(() => document.getElementById('file-upload')?.click(), 150);
        return () => clearTimeout(t);
    }, []);

    const isApprovedDoc = selectedDoc?.status === 'approved';
    const isReviewDoc = selectedDoc?.status === 'review';
    const isFiledDoc = selectedDoc?.status === 'filed';

    // Load the posting line and the client's own chart of accounts for the open document.
    useEffect(() => {
        if (!selectedDoc?.id) { setDocLine(null); setLineMeta(null); setLineEdits({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await documentLinesAPI.getLines(selectedDoc.id);
                if (cancelled) return;
                setLineMeta(r.data);
                setDocLine((r.data?.lines || [])[0] || null);
                setLineEdits({});
            } catch {
                if (!cancelled) { setDocLine(null); setLineMeta(null); }
            }
        })();
        return () => { cancelled = true; };
    }, [selectedDoc?.id]);

    useEffect(() => {
        const tid = selectedDoc?.tenant_id;
        if (!tid) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await documentLinesAPI.clientChart(tid);
                if (!cancelled) setClientChart({ accounts: r.data?.accounts || [], note: r.data?.note || null,
                                                 reason: r.data?.reason || null });
            } catch {
                if (!cancelled) setClientChart({ accounts: [], note: 'Kontoplanen kunne ikke hentes — indtast kontoen manuelt.' });
            }
        })();
        return () => { cancelled = true; };
    }, [selectedDoc?.tenant_id]);

    // Approve from the modal, using the same path as the single-screen review. Provenance is
    // recorded as multi_step: the modal is reached from the old document list, so crediting it to
    // the new screen would misreport which flow the accountant actually used.
    const handleApproveFromModal = async () => {
        if (!selectedDoc || !isReviewDoc) return;
        // Same rule as the single-screen review: never approve an unconfirmed sale/purchase type.
        if (lineMeta?.document_type_needs_confirmation || !lineMeta?.document_type) {
            toast.error('Bekræft bilagstypen (salg eller køb) i gennemgangen før godkendelse.');
            return;
        }
        if (!window.confirm(`Godkend "${selectedDoc.filename}"?`)) return;
        setApproving(true);
        try {
            if (Object.keys(editedFields).length > 0) {
                await documentAPI.editFields(selectedDoc.id, editedFields, false);
            }
            if (Object.keys(lineEdits).length > 0 && docLine) {
                await documentLinesAPI.saveLines(selectedDoc.id, [{ ...docLine, ...lineEdits }], false);
            }
            await documentLinesAPI.approveDocument(selectedDoc.id, 'multi_step');
            toast.success('Bilag godkendt');
            setIsDetailOpen(false);
            setEditedFields({});
            setLineEdits({});
            fetchDocuments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Kunne ikke godkende bilaget');
        } finally {
            setApproving(false);
        }
    };

    const lineValue = (k) => (lineEdits[k] !== undefined ? lineEdits[k] : (docLine?.[k] ?? ''));
    const setLineField = (k) => (v) => setLineEdits((e) => ({ ...e, [k]: v }));
    const hasFieldEdits = Object.keys(editedFields).length > 0;
    const hasLineEdits = Object.keys(lineEdits).length > 0;

    const handleSaveEdits = async () => {
        if (!selectedDoc) return;
        if (Object.keys(editedFields).length === 0 && Object.keys(lineEdits).length === 0) return;
        // Editing an already-approved document is a deliberate act: confirm, then send the explicit
        // flag the server requires. The server audits it separately and never un-approves silently.
        if (isApprovedDoc && !window.confirm(
            'Dette bilag er allerede godkendt. Vil du gemme ændringen? Handlingen registreres i revisionssporet.')) {
            return;
        }
        setSavingEdits(true);
        try {
            if (hasFieldEdits) {
                await documentAPI.editFields(selectedDoc.id, editedFields, isApprovedDoc);
            }
            if (hasLineEdits && docLine) {
                await documentLinesAPI.saveLines(selectedDoc.id, [{ ...docLine, ...lineEdits }], isApprovedDoc);
            }
            const response = await documentAPI.getOne(selectedDoc.id);
            setSelectedDoc(response.data);
            setEditedFields({});
            if (hasLineEdits) {
                const lr = await documentLinesAPI.getLines(selectedDoc.id);
                setLineMeta(lr.data);
                setDocLine((lr.data?.lines || [])[0] || null);
                setLineEdits({});
            }
            toast.success(isApprovedDoc ? 'Ændringer gemt og registreret' : 'Ændringer gemt');
            fetchDocuments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Kunne ikke gemme ændringerne');
        } finally {
            setSavingEdits(false);
        }
    };

    const handleApprove = async (approved) => {
        setApproving(true);
        try {
            // Merge edited fields with extracted data for final submission
            const finalData = {
                ...selectedDoc.extracted_data,
                ...editedFields
            };
            
            // Send the accountant's (possibly overridden) account + VAT mapping.
            // Falls back to the AI suggestion when not changed. The backend records
            // this against the AI's original suggestion to feed vendor-learning.
            const ai = selectedDoc.ai_suggestions || {};
            const accountMapping = {
                account_code: accountOverride.account_code || ai.account_code || '',
                account_name: accountOverride.account_name || ai.account_name || '',
                vat_code: accountOverride.vat_code || ai.vat_code || ''
            };

            const result = await documentAPI.approve(selectedDoc.id, approved, finalData, accountMapping);
            
            if (approved && result.data.voucher_id) {
                toast.success(
                    <div>
                        <p className="font-semibold">Invoice approved!</p>
                        <p className="text-sm">Draft voucher created and ready to push.</p>
                    </div>
                );
            } else if (!approved) {
                toast.info('Document rejected');
            }
            
            fetchDocuments();
            setIsDetailOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Action failed');
        } finally {
            setApproving(false);
        }
    };

    const filteredDocs = documents.filter(doc => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            doc.filename?.toLowerCase().includes(query) ||
            doc.extracted_data?.supplier_name?.toLowerCase().includes(query) ||
            doc.extracted_data?.invoice_number?.toLowerCase().includes(query)
        );
    });

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <CheckCircle className="h-4 w-4 text-success" />;
            case 'rejected': return <XCircle className="h-4 w-4 text-destructive" />;
            case 'review': return <Clock className="h-4 w-4 text-warning" />;
            case 'error': return <AlertTriangle className="h-4 w-4 text-destructive" />;
            default: return <Clock className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const getConfidenceIndicator = (confidence) => {
        if (confidence >= 0.8) return { color: 'bg-success', label: 'High' };
        if (confidence >= 0.5) return { color: 'bg-warning', label: 'Medium' };
        return { color: 'bg-destructive', label: 'Low' };
    };

    if (!currentTenant) {
        return (
            <div className="space-y-8" data-testid="documents-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Documents</h1>
                    <p className="text-muted-foreground mt-1">Upload and process invoices with AI</p>
                </div>
                <Card className="border-dashed border-2">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <AlertTriangle className="h-12 w-12 text-warning mb-4" />
                        <h3 className="font-heading text-xl font-semibold mb-2">No Company Selected</h3>
                        <p className="text-muted-foreground text-center mb-6">
                            Please select or create a company to manage documents
                        </p>
                        <Button onClick={() => navigate('/companies')}>
                            Go to Companies
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="documents-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Documents</h1>
                    <p className="text-muted-foreground mt-1">
                        Upload and process invoices for {currentTenant.name}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Button variant="outline" onClick={openExport} data-testid="export-economic-btn">
                        <Download className="h-4 w-4 mr-2" /> Export → e-conomic CSV
                    </Button>
                    <Select value={intakeKey} onValueChange={setIntakeKey} disabled={uploading}>
                        <SelectTrigger className="w-44" data-testid="upload-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept={uploadCategory === 'bank_statement' ? '.csv,.pdf,text/csv,application/pdf' : '.jpg,.jpeg,.png,.pdf'}
                        multiple={uploadCategory !== 'bank_statement'}
                        onChange={(e) => { handleFilesUpload(e.target.files); e.target.value = ''; }}
                        data-testid="file-upload-input"
                    />
                    <Button
                        onClick={() => document.getElementById('file-upload')?.click()}
                        disabled={uploading}
                        data-testid="upload-btn"
                    >
                        {uploading ? (
                            <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                            <Upload className="h-4 w-4 mr-2" />
                        )}
                        {uploading
                            ? `Uploading ${uploadQueue.filter(f => f.status === 'done' || f.status === 'failed').length}/${uploadQueue.length}…`
                            : (uploadCategory === 'bank_statement' ? 'Upload Statement' : uploadCategory === 'other' ? 'Upload Documents' : 'Upload Invoices')}
                    </Button>
                </div>
            </div>

            {/* Processing-queue progress (Part B) — visible movement during a large run */}
            {queue && queue.total > 0 && (
                <div className={`rounded-md border p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${queue.pending > 0 ? 'border-primary/30 bg-primary/5' : 'border-emerald-200 bg-emerald-50'}`} data-testid="queue-progress">
                    {queue.pending > 0
                        ? <span className="inline-flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Processing</span>
                        : <span className="text-emerald-700 font-medium">All processed</span>}
                    <span><strong>{queue.processed}</strong> of <strong>{queue.total}</strong> processed</span>
                    {queue.pending > 0 && <span className="text-muted-foreground">{queue.pending} pending{queue.rate_limited > 0 ? ` (${queue.rate_limited} paced)` : ''}</span>}
                    {queue.failed > 0 && <span className="text-red-600">{queue.failed} failed</span>}
                </div>
            )}

            {/* Bulk upload drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                onDrop={onDrop}
                onClick={() => { if (!uploading) document.getElementById('file-upload')?.click(); }}
                className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'} ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="upload-dropzone"
            >
                <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Drag & drop invoices here, or click to select</p>
                <p className="text-xs text-muted-foreground mt-1">
                    Multiple files supported — PDF, JPG, PNG. Each invoice is processed individually.
                </p>
            </div>

            {/* Per-file upload progress */}
            {uploadQueue.length > 0 && (
                <div className="rounded-lg border bg-muted/20 p-4 space-y-2" data-testid="upload-progress">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">
                            Upload progress — {uploadQueue.filter(f => f.status === 'done').length} done
                            {uploadQueue.some(f => f.status === 'failed') ? `, ${uploadQueue.filter(f => f.status === 'failed').length} failed` : ''}
                            {' '}/ {uploadQueue.length}
                        </h4>
                        {!uploading && (
                            <Button variant="ghost" size="sm" onClick={() => setUploadQueue([])} data-testid="clear-upload-progress">
                                Clear
                            </Button>
                        )}
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                        {uploadQueue.map((f, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0">
                                <span className="flex items-center gap-2 min-w-0 mr-2">
                                    {f.status === 'queued' && <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                    {f.status === 'uploading' && <div className="w-3.5 h-3.5 shrink-0 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
                                    {f.status === 'done' && <CheckCircle className="h-3.5 w-3.5 shrink-0 text-success" />}
                                    {f.status === 'failed' && <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
                                    <span className="truncate">{f.name}</span>
                                </span>
                                <span className={`text-xs whitespace-nowrap ${f.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>
                                    {f.status === 'failed' ? (f.error || 'failed') : f.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search documents..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="search-documents"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="status-filter">
                        <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="review">Pending Review</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Documents Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : filteredDocs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">No documents found</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="data-table-header">
                                    <TableHead>Document</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Invoice #</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>AI Confidence</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead className="w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <motion.tbody
                                    variants={container}
                                    initial="hidden"
                                    animate="show"
                                    className="contents"
                                >
                                    {filteredDocs.map((doc) => {
                                        const statusInfo = getStatusBadge(doc.status);
                                        const confidenceInfo = getConfidenceIndicator(doc.overall_confidence || 0);
                                        return (
                                            <motion.tr
                                                key={doc.id}
                                                variants={item}
                                                className="data-table-row"
                                            >
                                                <TableCell className="data-table-cell">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                                        <span className="font-medium truncate max-w-[200px]">
                                                            {doc.filename}
                                                            <SourceBadge source={doc.source} className="ml-2 align-middle" />
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="data-table-cell">
                                                    {doc.extracted_data?.supplier_name || '-'}
                                                </TableCell>
                                                <TableCell className="data-table-cell font-mono">
                                                    {doc.extracted_data?.invoice_number || '-'}
                                                </TableCell>
                                                <TableCell className="data-table-cell font-mono">
                                                    {formatCurrency(doc.extracted_data?.total_amount, doc.extracted_data?.currency)}
                                                    <PagesWarning doc={doc} compact className="ml-2 align-middle" />
                                                </TableCell>
                                                <TableCell className="data-table-cell">
                                                    {doc.overall_confidence !== null && doc.overall_confidence !== undefined && (
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`w-2 h-2 rounded-full ${confidenceInfo.color}`} />
                                                                        <span className="font-mono">
                                                                            {Math.round(doc.overall_confidence * 100)}%
                                                                        </span>
                                                                        {doc.uncertain_fields?.length > 0 && (
                                                                            <AlertTriangle className="h-3 w-3 text-warning" />
                                                                        )}
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {doc.uncertain_fields?.length > 0 
                                                                        ? `${doc.uncertain_fields.length} fields need review`
                                                                        : 'All fields confident'
                                                                    }
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    )}
                                                </TableCell>
                                                <TableCell className="data-table-cell">
                                                    <div className="flex items-center gap-1.5">
                                                        <Badge variant={statusInfo.variant}>
                                                            {getStatusIcon(doc.status)}
                                                            <span className="ml-1">{statusInfo.label}</span>
                                                        </Badge>
                                                        {doc.delivered_to_inbox && (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="inline-flex items-center text-success" data-testid={`inbox-delivered-${doc.id}`}>
                                                                            <MailCheck className="h-4 w-4" />
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        Delivered to scan inbox
                                                                        {doc.delivered_to_inbox_email ? ` (${doc.delivered_to_inbox_email})` : ''}
                                                                        {doc.delivered_to_inbox_at ? ` — ${formatDate(doc.delivered_to_inbox_at)}` : ''}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="data-table-cell text-muted-foreground">
                                                    {formatDate(doc.created_at)}
                                                </TableCell>
                                                <TableCell className="data-table-cell">
                                                    <div className="flex items-center gap-1">
                                                        {doc.status === 'review' ? (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openDocumentDetail(doc)}
                                                                data-testid={`review-doc-${doc.id}`}
                                                            >
                                                                <Edit2 className="h-4 w-4 mr-1" />
                                                                Review
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => openDocumentDetail(doc)}
                                                                data-testid={`view-doc-${doc.id}`}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        {(doc.doc_category === 'invoice' || !doc.doc_category) && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => navigate(`/app/documents/${doc.id}/review`)}
                                                                data-testid={`lines-doc-${doc.id}`}
                                                                title="Line-by-line review & adjust"
                                                            >
                                                                <FileText className="h-4 w-4 mr-1" /> Lines
                                                            </Button>
                                                        )}
                                                        {doc.status !== 'approved' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                onClick={() => setDocToDelete(doc)}
                                                                title="Delete this document"
                                                                data-testid={`delete-doc-${doc.id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </motion.tr>
                                        );
                                    })}
                                </motion.tbody>
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Document Detail Dialog — same side-by-side layout as the single-screen review:
                bilag left, fields right, both visible without scrolling the dialog. This is the
                only way to view an ALREADY-APPROVED document, so fields stay editable here, behind
                an explicit save. */}
            <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
                    <DialogHeader className="px-5 py-3 border-b shrink-0">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <FileText className="h-4 w-4" />
                            {selectedDoc?.filename}
                            {selectedDoc && (
                                <Badge variant={getStatusBadge(selectedDoc.status).variant} className="ml-2">
                                    {getStatusBadge(selectedDoc.status).label}
                                </Badge>
                            )}
                        </DialogTitle>
                        <DialogDescription className="sr-only">Bilag og udtrukne felter</DialogDescription>
                    </DialogHeader>

                    {selectedDoc && (
                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
                            {/* LEFT — the bilag, in our own viewer */}
                            <div className="border-r min-h-0 flex flex-col">
                                <BilagViewer docId={selectedDoc.id} filename={selectedDoc.filename}
                                             fileType={selectedDoc.file_type} className="flex-1" />
                            </div>

                            {/* RIGHT — extracted fields */}
                            <div className="flex flex-col min-h-0">
                                <div className="flex-1 overflow-auto p-5 space-y-4">
                                    {/* Visible failure reason — never leave the user guessing */}
                                    {['failed', 'ai_processing_failed', 'unreadable'].includes(selectedDoc.status) && (
                                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" data-testid="doc-error-banner">
                                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                            <span>{selectedDoc.error_message || 'This document could not be processed. Please re-upload a clearer copy.'}</span>
                                        </div>
                                    )}

                                    {isApprovedDoc && (
                                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
                                             data-testid="approved-edit-note">
                                            Dette bilag er allerede godkendt. Ændringer gemmes kun, når du trykker
                                            «Gem ændringer», og bliver registreret i revisionssporet.
                                        </div>
                                    )}

                                    {/* POSTING LINE — what actually reaches e-conomic / Uniconta */}
                                    <div className="rounded-lg border p-3 space-y-3" data-testid="modal-posting-line">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                                Posteringslinje (leveres til bogføringssystemet)
                                            </h4>
                                            <span className="text-[11px] text-muted-foreground">
                                                Bilagsnr. {lineMeta?.bilagsnummer ?? '—'}
                                                {lineMeta?.bilags_provisional ? ' (foreløbigt)' : ''}
                                            </span>
                                        </div>
                                        {!docLine ? (
                                            <p className="text-xs text-muted-foreground">Ingen posteringslinje endnu.</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="col-span-2">
                                                    <Label className={fieldLabelClass(false)}>Konto</Label>
                                                    <select
                                                        className={`${fieldInputClass(false)} mt-1`}
                                                        value={String(lineValue('account') || '')}
                                                        onChange={(e) => setLineField('account')(e.target.value)}
                                                        data-testid="modal-line-account"
                                                    >
                                                        <option value="">— vælg konto —</option>
                                                        {clientChart.accounts.map((a) => (
                                                            <option key={String(a.konto)} value={String(a.konto)}>
                                                                {a.konto} — {a.navn}
                                                            </option>
                                                        ))}
                                                        {/* Keep an account that is not in the fetched chart visible rather than silently blank */}
                                                        {lineValue('account') && !clientChart.accounts.some((a) => String(a.konto) === String(lineValue('account'))) && (
                                                            <option value={String(lineValue('account'))}>
                                                                {lineValue('account')} — (ikke i kontoplanen)
                                                            </option>
                                                        )}
                                                    </select>
                                                </div>
                                                <div>
                                                    <Label className={fieldLabelClass(false)}>Modkonto</Label>
                                                    <input className={`${fieldInputClass(false)} mt-1`}
                                                           value={lineValue('contra_account')}
                                                           onChange={(e) => setLineField('contra_account')(e.target.value)}
                                                           data-testid="modal-line-contra" />
                                                </div>
                                                <div>
                                                    <Label className={fieldLabelClass(false)}>Momskode</Label>
                                                    <input className={`${fieldInputClass(false)} mt-1`}
                                                           value={lineValue('vat_code')}
                                                           onChange={(e) => setLineField('vat_code')(e.target.value)}
                                                           data-testid="modal-line-vat" />
                                                </div>
                                                <div>
                                                    <Label className={fieldLabelClass(false)}>Beløb (leveret)</Label>
                                                    <input className={`${fieldInputClass(false)} mt-1`} type="number" step="0.01"
                                                           value={lineValue('amount')}
                                                           onChange={(e) => setLineField('amount')(
                                                               e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                           data-testid="modal-line-amount" />
                                                </div>
                                                <div>
                                                    <Label className={fieldLabelClass(false)}>Dato (leveret)</Label>
                                                    <input className={`${fieldInputClass(false)} mt-1`}
                                                           value={lineValue('date')}
                                                           onChange={(e) => setLineField('date')(e.target.value)}
                                                           data-testid="modal-line-date" />
                                                </div>
                                                <div className="col-span-2">
                                                    <Label className={fieldLabelClass(false)}>Tekst</Label>
                                                    <input className={`${fieldInputClass(false)} mt-1`}
                                                           value={lineValue('text')}
                                                           onChange={(e) => setLineField('text')(e.target.value)}
                                                           data-testid="modal-line-text" />
                                                </div>
                                            </div>
                                        )}
                                        {(() => {
                                            const msg = vatContradiction(
                                                lineValue('vat_code'),
                                                (selectedDoc.extracted_data || {}).vat_amount);
                                            return msg ? (
                                                <p className="text-[11px] text-amber-700 rounded-md border border-amber-300 bg-amber-50 p-2"
                                                   data-testid="modal-vat-contradiction">
                                                    {msg} Kontroller momskoden og momsbeløbet.
                                                </p>
                                            ) : null;
                                        })()}
                                        {clientChart.note && (
                                            <p className="text-[11px] text-amber-600" data-testid="modal-chart-note">
                                                {clientChart.note}
                                                {['reconnect_required', 'not_connected'].includes(clientChart.reason) && (
                                                    <>
                                                        {' '}
                                                        <a href="/app/settings" className="underline font-medium"
                                                           data-testid="modal-chart-reconnect-link">
                                                            Åbn Indstillinger
                                                        </a>
                                                    </>
                                                )}
                                            </p>
                                        )}
                                    </div>

                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">
                                        Aflæst fra bilaget
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {Object.entries(FIELD_LABELS).map(([field, label]) => {
                                            if (field === 'line_items') return null;
                                            const uncertain = isFieldUncertain(selectedDoc, field);
                                            const value = editedFields[field] ?? selectedDoc.extracted_data?.[field] ?? '';
                                            const isNum = ['net_amount', 'vat_amount', 'total_amount'].includes(field);
                                            return (
                                                <div key={field}>
                                                    <Label className={fieldLabelClass(uncertain)}>{label}</Label>
                                                    <input
                                                        className={`${fieldInputClass(uncertain)} mt-1`}
                                                        type={isNum ? 'number' : 'text'}
                                                        step={isNum ? '0.01' : undefined}
                                                        value={value}
                                                        onChange={(e) => setEditedFields((f) => ({
                                                            ...f,
                                                            [field]: isNum
                                                                ? (e.target.value === '' ? '' : parseFloat(e.target.value))
                                                                : e.target.value,
                                                        }))}
                                                        data-testid={`modal-field-${field}`}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Explicit save — never auto-save */}
                                <div className="border-t p-4 flex items-center gap-2 shrink-0 bg-background">
                                    {isReviewDoc && (
                                        <Button onClick={handleApproveFromModal}
                                                disabled={approving || savingEdits
                                                          || lineMeta?.document_type_needs_confirmation
                                                          || !lineMeta?.document_type}
                                                title={lineMeta?.document_type_needs_confirmation
                                                       ? 'Bekræft bilagstypen først' : undefined}
                                                data-testid="modal-approve-btn">
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            {approving ? 'Godkender…' : 'Godkend'}
                                        </Button>
                                    )}
                                    <Button onClick={handleSaveEdits}
                                            variant={isReviewDoc ? 'outline' : 'default'}
                                            disabled={savingEdits || approving || isFiledDoc || (!hasFieldEdits && !hasLineEdits)}
                                            data-testid="modal-save-btn">
                                        {savingEdits ? 'Gemmer…' : 'Gem ændringer'}
                                    </Button>
                                    {(hasFieldEdits || hasLineEdits) && (
                                        <Button variant="ghost" onClick={() => { setEditedFields({}); setLineEdits({}); }}
                                                disabled={savingEdits} data-testid="modal-discard-btn">
                                            Fortryd
                                        </Button>
                                    )}
                                    <span className="ml-auto text-[11px] text-muted-foreground">
                                        {isFiledDoc
                                            ? 'Leveret — kan ikke ændres'
                                            : (!hasFieldEdits && !hasLineEdits)
                                                ? 'Ingen ændringer'
                                                : `${Object.keys(editedFields).length + Object.keys(lineEdits).length} felt(er) ændret`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete document confirmation */}
            <AlertDialog open={!!docToDelete} onOpenChange={(o) => { if (!o) setDocToDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this document?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {docToDelete && (<>This removes <span className="font-medium">{docToDelete.filename || 'this document'}</span> from the queue and the client's inbox. This cannot be undone. Approved/booked documents cannot be deleted.</>)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingDoc}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); handleDeleteDoc(); }} disabled={deletingDoc} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-doc">
                            {deletingDoc ? 'Deleting…' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Phase 2a — batch e-conomic kassekladde CSV export (per client) */}
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Export to e-conomic (kassekladde CSV)</DialogTitle>
                        <DialogDescription>
                            Approved, balanced documents for {currentTenant?.name} that haven't been exported yet.
                            One CSV row per line, ordered by bilagsnummer. Import it into e-conomic — nothing is booked automatically.
                        </DialogDescription>
                    </DialogHeader>
                    {!pending ? (
                        <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <span className="font-medium">{pending.count}</span> document(s) ready · <span className="font-medium">{pending.rows}</span> line(s).
                            </div>
                            {pending.exportable.length > 0 && (
                                <div className="border border-border rounded-md divide-y max-h-48 overflow-y-auto">
                                    {pending.exportable.map((d) => (
                                        <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                            <span className="min-w-0 truncate">{d.bilagsnummer != null ? `#${d.bilagsnummer} · ` : ''}{d.supplier || 'Document'}</span>
                                            <span className="text-muted-foreground shrink-0 whitespace-nowrap">{d.lines} line(s)</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {pending.skipped.length > 0 && (
                                <div className="text-xs text-warning space-y-1">
                                    <p className="font-medium">{pending.skipped.length} skipped (not exported):</p>
                                    {pending.skipped.map((d) => (
                                        <p key={d.id} className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {d.supplier || 'Document'} — {d.reason}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExportOpen(false)} disabled={exportingBatch}>Cancel</Button>
                        <Button onClick={runBatchExport} disabled={exportingBatch || !pending || pending.count === 0} data-testid="download-batch-csv">
                            <Download className="h-4 w-4 mr-2" /> {exportingBatch ? 'Exporting…' : `Download CSV${pending?.count ? ` (${pending.count})` : ''}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
